// Auto-generates the short "tag name" shown on price labels and in the big
// title slot of the detail view. The seed value — a user override always wins.
//
// Per-kind formulas:
//   - serialized_firearm: {manufacturer} {model} {caliber} {barrel}" {capacity}rd
//                          {finish} + features. Capped at 50 (fits firearm e-tag).
//   - ammo (presence of ammo inputs):
//                          {manufacturer} {caliber} {grain}gr {bullet_type}
//                          {rounds}rd. Capped at 32 (paper tag).
//   - optics (presence of optics inputs):
//                          {manufacturer} {model} {magnification} {reticle}.
//                          Capped at 40 (paper tag).
//   - holster (presence of holster inputs):
//                          {manufacturer} {fit_model} {mount_type} {draw_side}.
//                          Capped at 36 (paper tag).
//   - everything else: fall back to the user-typed displayName capped at 22.

export type TagNameLimits = {
  firearm: number;
  ammo: number;
  optics: number;
  holster: number;
  default: number;
};

export const DEFAULT_TAG_NAME_LIMITS: TagNameLimits = {
  firearm: 50,
  ammo: 32,
  optics: 40,
  holster: 36,
  default: 22,
};

export type FirearmTagInputs = {
  manufacturer: string | null;
  model: string | null;
  caliberGauge: string | null;
  barrelLengthIn: string | null;
  finish: string | null;
  features: string[];
  // Round count. Always shown in the tag as "15rd" / "5rd" / etc. when set,
  // regardless of whether the mag is fixed or detachable (owner directive
  // 2026-10-30). For detachable-mag guns this reports what the included mag
  // holds; for fixed-mag guns it reports the chamber/tube capacity.
  magazineCapacity: number | null;
};

export type AmmoTagInputs = {
  manufacturer: string | null;
  caliberGauge: string | null;
  grain: number | null;
  bulletType: string | null;
  roundsPerBox: number | null;
};

export type OpticsTagInputs = {
  manufacturer: string | null;
  model: string | null;
  magnification: string | null;
  reticle: string | null;
};

export type HolsterTagInputs = {
  manufacturer: string | null;
  fitModel: string | null;
  mountType: string | null;
  drawSide: string | null;
};

export type TagNameInput = {
  kind: string;
  displayName: string;
  override: string | null;
  firearm?: FirearmTagInputs | null;
  ammo?: AmmoTagInputs | null;
  optics?: OpticsTagInputs | null;
  holster?: HolsterTagInputs | null;
};

export type TagNameSource = "override" | "formula" | "displayName";
export type TagNameKindUsed = "firearm" | "ammo" | "optics" | "holster" | "default";

export type TagNameResult = {
  value: string;
  source: TagNameSource;
  truncated: boolean;
};

export function generateTagName(
  input: TagNameInput,
  limits: TagNameLimits = DEFAULT_TAG_NAME_LIMITS,
): TagNameResult {
  const kindUsed = resolveKindUsed(input);
  const cap = capFor(kindUsed, limits);

  if (input.override?.trim()) {
    return finalize(input.override.trim(), cap, "override");
  }

  const formula = formulaFor(input, kindUsed);
  if (formula) return finalize(formula, cap, "formula");

  return finalize(input.displayName, cap, "displayName");
}

// Picks the per-kind formula slot to use. The firearm slot is tied to
// item.kind (serialized_firearm) because the bound-book invariant requires
// it; ammo/optics/holster are signaled by the presence of their inputs
// (same way the DB uses "row exists in item_ammo_attributes" as the signal
// that an item is structured ammo).
function resolveKindUsed(input: TagNameInput): TagNameKindUsed {
  if (input.kind === "serialized_firearm" && input.firearm) return "firearm";
  if (input.ammo) return "ammo";
  if (input.optics) return "optics";
  if (input.holster) return "holster";
  return "default";
}

function capFor(kindUsed: TagNameKindUsed, limits: TagNameLimits): number {
  switch (kindUsed) {
    case "firearm":
      return limits.firearm;
    case "ammo":
      return limits.ammo;
    case "optics":
      return limits.optics;
    case "holster":
      return limits.holster;
    default:
      return limits.default;
  }
}

function formulaFor(input: TagNameInput, kindUsed: TagNameKindUsed): string | null {
  switch (kindUsed) {
    case "firearm":
      return input.firearm ? firearmFormula(input.firearm) : null;
    case "ammo":
      return input.ammo ? ammoFormula(input.ammo) : null;
    case "optics":
      return input.optics ? opticsFormula(input.optics) : null;
    case "holster":
      return input.holster ? holsterFormula(input.holster) : null;
    default:
      return null;
  }
}

function firearmFormula(f: FirearmTagInputs): string | null {
  const capacity =
    f.magazineCapacity != null && f.magazineCapacity > 0 ? `${f.magazineCapacity}rd` : null;
  const base = [
    f.manufacturer,
    f.model,
    f.caliberGauge,
    formatBarrel(f.barrelLengthIn),
    capacity,
    f.finish,
  ]
    .map((p) => p?.trim())
    .filter((p): p is string => !!p && p.length > 0)
    .join(" ");

  if (!base) return null;

  const features = f.features
    .map((t) => t.trim())
    .filter((t) => t.length > 0)
    .join(" ");

  return features ? `${base} ${features}` : base;
}

function ammoFormula(a: AmmoTagInputs): string | null {
  const grain = a.grain != null && a.grain > 0 ? `${a.grain}gr` : null;
  const rounds = a.roundsPerBox != null && a.roundsPerBox > 0 ? `${a.roundsPerBox}rd` : null;
  const bullet = a.bulletType ? a.bulletType.toUpperCase() : null;
  return joinParts([a.manufacturer, a.caliberGauge, grain, bullet, rounds]);
}

function opticsFormula(o: OpticsTagInputs): string | null {
  return joinParts([o.manufacturer, o.model, o.magnification, o.reticle]);
}

function holsterFormula(h: HolsterTagInputs): string | null {
  const mount = h.mountType ? h.mountType.toUpperCase() : null;
  const side = h.drawSide ? abbreviateDrawSide(h.drawSide) : null;
  return joinParts([h.manufacturer, h.fitModel, mount, side]);
}

function abbreviateDrawSide(side: string): string {
  const key = side.trim().toLowerCase();
  if (key === "left") return "LH";
  if (key === "right") return "RH";
  if (key === "ambidextrous") return "AMBI";
  return side.toUpperCase();
}

function joinParts(parts: Array<string | null>): string | null {
  const kept = parts.map((p) => p?.trim()).filter((p): p is string => !!p && p.length > 0);
  if (kept.length === 0) return null;
  return kept.join(" ");
}

/**
 * Format an inch value for presentation. Strips meaningless trailing zeros so
 * `16.00` renders as `16"` and `3.50` as `3.5"`, while preserving `4.02"` and
 * `18.5"`. If the input already carries a trailing `"`, don't double-quote.
 * Shared between the tag-name formula and the detail-view display.
 */
export function formatInches(value: string | null): string | null {
  if (!value) return null;
  let trimmed = value.trim();
  if (!trimmed) return null;
  if (trimmed.endsWith('"')) trimmed = trimmed.slice(0, -1).trimEnd();
  const parsed = Number(trimmed);
  const num = Number.isFinite(parsed) ? String(parsed) : trimmed;
  return `${num}"`;
}

const formatBarrel = formatInches;

function finalize(raw: string, cap: number, source: TagNameSource): TagNameResult {
  const trimmed = raw.trim();
  if (trimmed.length <= cap) {
    return { value: trimmed, source, truncated: false };
  }
  const truncated = `${trimmed.slice(0, Math.max(0, cap - 1)).trimEnd()}…`;
  return { value: truncated, source, truncated: true };
}
